import { hardNavigate } from './hardNavigate'

// In dev: empty string (Vite proxy handles /api/... routes)
// In staging: https://stagingapi.puresoft.ca (set via VITE_API_BASE_URL at build time)
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// ── 401 handler — auto-clears stale auth on unauthorized response ────
// A 401 means the token that was SENT is no longer accepted. What that says about the stored
// session depends on whose token it was:
//   - The stored one, while impersonating: the impersonation session has ended (reverted in
//     another tab, signed out of, expired, or either person deactivated). The admin's own session
//     from before the impersonation is put back, if its token is still live, and the app reloads
//     as them — the same place Revert to Admin lands.
//   - The stored one otherwise: the session is over. Clear auth so the user is sent to sign in.
//   - A token the store no longer holds: another tab has since reverted or signed in, or a revert
//     in this tab raced a request still in flight. The stored session is not the one refused, so
//     it is left alone and the page reloads onto it.
// Public pages (roadmap, confirm) must not redirect unauthenticated visitors.
const AUTH_STORAGE_KEY = 'puresoft-auth'
const PUBLIC_PATHS = ['/roadmap', '/login', '/setup', '/never-out/silence']
function isPublicPath(pathname: string) {
    return PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

/** Time a restored token must still have left, so the reload does not land on a 401 of its own. */
const RESTORE_MARGIN_MS = 30_000

/**
 * True when `token` is a JWT whose `exp` is still ahead by the margin. Anything unreadable counts as
 * expired. Read without verifying the signature: this only decides whether putting the token back is
 * worth a try — the server still judges it on every request.
 */
function isLiveJwt(token: unknown): boolean {
    if (typeof token !== 'string') return false
    try {
        const payload = token.split('.')[1]
        if (!payload) return false
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
            .padEnd(Math.ceil(payload.length / 4) * 4, '=')
        const exp: unknown = JSON.parse(atob(base64))?.exp
        return typeof exp === 'number' && exp * 1000 > Date.now() + RESTORE_MARGIN_MS
    } catch {
        return false
    }
}

/**
 * @param failedToken The bearer token the refused request carried, or null when it is not known
 *   (or the request carried none) — then the stored session is taken to be the one refused.
 */
function handle401(failedToken: string | null) {
    const onPublicPage = isPublicPath(window.location.pathname)
    let destination: string | null = onPublicPage ? null : '/login'
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        const state = parsed?.state
        if (state && failedToken && state.accessToken && state.accessToken !== failedToken) {
            // Not the stored session: left as it is, and the page reloads onto it.
            destination = onPublicPage ? null : '/'
        } else if (state) {
            if (state.isImpersonating && state.originalUser && isLiveJwt(state.originalAccessToken)) {
                // Back to the admin who started the impersonation, exactly as Revert to Admin
                // would put them. Without this, the end of an impersonation signed the admin out.
                state.user = state.originalUser
                state.accessToken = state.originalAccessToken
                state.refreshToken = state.originalRefreshToken ?? null
                state.isAuthenticated = true
                if (!onPublicPage) destination = '/'
            } else {
                state.user = null
                state.accessToken = null
                state.refreshToken = null
                state.isAuthenticated = false
            }
            // Drop impersonation state either way. An impersonation session can't be refreshed,
            // so it 401s here; if we left these flags set, the next sign-in would inherit a
            // stale "isImpersonating" and show the user impersonating themselves.
            state.originalUser = null
            state.originalAccessToken = null
            state.originalRefreshToken = null
            state.isImpersonating = false
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed))
        }
    } catch { /* swallow */ }
    if (destination) hardNavigate(destination)
}

/** The bearer token a fetch call sent, or null when it sent none. */
function bearerOf(input: RequestInfo | URL, init?: RequestInit): string | null {
    try {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
        const value = headers.get('Authorization')
        return value?.startsWith('Bearer ') ? value.slice('Bearer '.length) : null
    } catch {
        return null
    }
}

// ── Intercept raw fetch calls globally to catch 401s ──
const originalFetch = window.fetch
window.fetch = async (...args) => {
    const res = await originalFetch(...args)
    if (res.status === 401) {
        handle401(bearerOf(...args))
    }
    return res
}

async function handleResponse(r: Response, token: string | null) {
    if (r.status === 401) {
        handle401(token)
        throw new Error('Session expired. Please sign in again.')
    }
    if (!r.ok) throw new Error(await r.text())
    return r.json()
}

export const api = (url: string, token: string | null) =>
    fetch(API_BASE + url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
        .then(r => handleResponse(r, token))

/** Uploads a single file as multipart/form-data (no Content-Type header — the browser sets the boundary). */
export const apiUpload = (url: string, token: string | null, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return fetch(API_BASE + url, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: fd,
    }).then(r => handleResponse(r, token))
}

/** Resolves a server-relative image path (e.g. /uploads/...) to an absolute URL on the API origin. */
export const imageUrl = (path: string) =>
    path.startsWith('http') ? path : API_BASE + path

export const apiPost = (url: string, token: string | null, body: any = null) =>
    fetch(API_BASE + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
    }).then(r => handleResponse(r, token))

export const apiPut = (url: string, token: string | null, body: any = null) =>
    fetch(API_BASE + url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
    }).then(r => handleResponse(r, token))

export const apiDelete = (url: string, token: string | null) =>
    fetch(API_BASE + url, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    }).then(r => handleResponse(r, token))
